import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { ALAIN_I18N_TOKEN, SettingsService, User } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { I18NService } from '../../../core/i18n/i18n.service';
import { BROADCAST_SERVICE_TOKEN } from '../../../core/services/helpers/broadcast.service';
import { LoadingService } from '../../../core/services/helpers/loading.service';
import { AuthService } from '../../../core/services/implements/auth.service';
import { PreferenceService } from '../../../core/services/implements/preference.service';
import { AccountState } from '../../../core/services/states/account.service';
import { ThemeService } from '../../../core/services/states/theme.service';
import { BroadCastChannelMessageTypeConstant } from '../../../shared/constants/broadcast-channel-message-type.constant';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';

@Component({
  selector: 'header-user',
  template: `
    <div class="alain-default__nav-item d-flex align-items-center px-sm" nz-dropdown nzPlacement="bottomRight" [nzDropdownMenu]="userMenu">
      <nz-avatar [nzSrc]="user.avatar" nzSize="small" class="mr-sm" />
      {{ user.name }}
    </div>
    <nz-dropdown-menu #userMenu="nzDropdownMenu">
      <div nz-menu class="width-sm">
        <div nz-menu-item [routerLink]="'/web/profile/' + accountState.get()?.userId">
          <i nz-icon nzType="user" class="mr-sm"></i>
          {{ 'label.profile' | i18n }}
        </div>
        <div nz-submenu [nzTitle]="langMenuTitle">
          <ul>
            @for (item of this.i18n.getLangs(); track $index) {
              <li nz-menu-item [nzSelected]="item.code === this.preferenceService.defaultLanguage" (click)="changeLanguage(item.code)">
                <span role="img" [attr.aria-label]="item.text" class="pr-xs">{{ item.abbr }}</span>
                {{ item.text | i18n }}
              </li>
            }
          </ul>
        </div>
        <div nz-submenu [nzTitle]="themeMenuTitle">
          <ul>
            @for (item of this.themeService.getThemes(); track $index) {
              <li nz-menu-item [nzSelected]="item.code === this.preferenceService.defaultTheme" (click)="changeTheme(item.code)">
                <span role="img" [attr.aria-label]="item.text" class="pr-xs"></span>
                {{ item.text | i18n }}
              </li>
            }
          </ul>
        </div>
        <li nz-menu-divider></li>
        <div nz-menu-item (click)="authService.logout(true)">
          <i nz-icon nzType="logout" class="mr-sm"></i>
          {{ 'menu.account.logout' | i18n }}
        </div>
      </div>
    </nz-dropdown-menu>
    <ng-template #langMenuTitle>
      <span class="d-inline-flex align-items-center">
        <i nz-icon nzType="global" class="mr-sm"></i>
        &nbsp;{{ 'label.lang' | i18n }}
      </span>
    </ng-template>
    <ng-template #themeMenuTitle>
      <span class="d-inline-flex align-items-center">
        <i nz-icon nzType="setting" class="mr-sm" nzTheme="outline"></i>
        &nbsp;{{ 'label.theme.title' | i18n }}
      </span>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NzDropDownModule, NzMenuModule, NzIconModule, I18nPipe, NzAvatarModule]
})
export class HeaderUserComponent implements OnInit {
  protected destroyRef = inject(DestroyRef);
  protected cdr = inject(ChangeDetectorRef);
  protected authService = inject(AuthService);
  protected settings = inject(SettingsService);
  protected loadingService = inject(LoadingService);
  protected themeService = inject(ThemeService);
  protected preferenceService = inject(PreferenceService);
  protected i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  protected broadcastService = inject(BROADCAST_SERVICE_TOKEN);
  protected accountState = inject(AccountState);
  ngOnInit(): void {
    this.settings.notify.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res.type === 'user') this.cdr.markForCheck();
    });
  }

  get user(): User {
    return this.settings.user;
  }
  changeLanguage(language: string): void {
    this.loadingService.show();
    this.preferenceService.updatePreference(language, this.preferenceService.defaultTheme, () => {
      setTimeout(() => {
        this.preferenceService
          .changeLanguage(language)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.broadcastService.publish({
              type: BroadCastChannelMessageTypeConstant.Theme,
              payload: null
            });
            this.loadingService.hide();
            this.cdr.detectChanges();
          });
      }, 1000);
    });
  }
  changeTheme(theme: string): void {
    this.loadingService.show();
    this.preferenceService.updatePreference(this.preferenceService.defaultLanguage, theme, () => {
      setTimeout(() => {
        this.preferenceService
          .changeTheme(theme)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.broadcastService.publish({
              type: BroadCastChannelMessageTypeConstant.Theme,
              payload: null
            });
            this.loadingService.hide();
            this.cdr.detectChanges();
          });
      }, 1000);
    });
  }
}
